// ─────────────────────────────────────────────────────────────────────────────
// COUNTS-ONLY TELEMETRY BEACON (Ticket 10) — flag-dark by default.
//
// The consented creator-insight channel: aggregate, k-anonymous counts.
// {candidateSetVersion, cardId|topic, event: viewed|ranked-top3|clicked|asked}
// with NO user identifier — a session-scoped RANDOM id, minted per page load,
// held only in memory (never localStorage, never a cookie), meaningless
// outside the server's k-distinct count.
//
// Origin typing, completing the fence family:
//   show origin        bare anonymous GETs           (showFence)
//   runtime origin     identity + preference         (the fan's own agent)
//   member service     identity, never preference    (memberDoor)
//   telemetry origin   preference-in-aggregate-shape, NEVER identity  (THIS)
//
// The beacon may carry a topic (that is the point — counts of what a room
// cares about) but must structurally never carry who: no bearer, no cookies,
// no identifier-shaped fields or values. `assertCountsOnlyBeacon` is the
// client-side tripwire; the server re-validates and rejects whole.
//
// FLAG: VITE_TELEMETRY_COUNTS_ONLY === 'true' AND VITE_TELEMETRY_URL set.
// Default OFF — emit() is a no-op and no telemetry code path runs.
// ─────────────────────────────────────────────────────────────────────────────

export type TelemetryEvent = 'viewed' | 'ranked-top3' | 'clicked' | 'asked'

export class TelemetryViolation extends Error {
  constructor(message: string) {
    super(`telemetry fence violation: ${message}`)
    this.name = 'TelemetryViolation'
  }
}

const ALLOWED_FIELDS = new Set(['candidateSetVersion', 'cardId', 'topic', 'event', 'sessionId', 'question'])

export function telemetryEnabled(): boolean {
  return import.meta.env?.VITE_TELEMETRY_COUNTS_ONLY === 'true' && Boolean(import.meta.env?.VITE_TELEMETRY_URL)
}

/** Session-scoped random id: per page load, memory only, rotated per visit. */
let sessionId: string | null = null
export function telemetrySessionId(): string {
  if (!sessionId) {
    const bytes = new Uint8Array(16)
    crypto.getRandomValues(bytes)
    sessionId = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
  }
  return sessionId
}

/** Client-side mirror of the server's identifier rejection. */
export function assertCountsOnlyBeacon(beacon: Record<string, unknown>): void {
  for (const [k, v] of Object.entries(beacon)) {
    if (!ALLOWED_FIELDS.has(k)) {
      throw new TelemetryViolation(`field "${k}" is not part of the counts-only contract`)
    }
    const s = String(v ?? '')
    if (k !== 'question' && (/^did:/.test(s) || s.includes('@') || /\d{7,}/.test(s))) {
      throw new TelemetryViolation(`field "${k}" carries an identifier-shaped value`)
    }
  }
  if (!beacon.event) throw new TelemetryViolation('event required')
}

/**
 * Fire one beacon. No-op when the flag is dark. Never throws into the UI —
 * a beacon must never cost the page anything.
 */
export function emit(
  event: TelemetryEvent,
  fields: {topic?: string; cardId?: string; candidateSetVersion?: number; question?: string} = {},
): void {
  if (!telemetryEnabled()) return
  try {
    const beacon: Record<string, unknown> = {event, sessionId: telemetrySessionId()}
    if (fields.topic) beacon.topic = String(fields.topic).toLowerCase()
    if (fields.cardId) beacon.cardId = fields.cardId
    if (fields.candidateSetVersion !== undefined) beacon.candidateSetVersion = fields.candidateSetVersion
    if (event === 'asked' && fields.question) {
      // normalize client-side too: the raw ask text never leaves the page
      beacon.question = fields.question
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s]/gu, '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 120)
    }
    assertCountsOnlyBeacon(beacon)
    void fetch(`${import.meta.env.VITE_TELEMETRY_URL}/telemetry/beacon`, {
      method: 'POST',
      credentials: 'omit', // never a cookie
      keepalive: true,
      headers: {'content-type': 'application/json'}, // never an authorization
      body: JSON.stringify(beacon),
    }).catch(() => {})
  } catch {
    // fence violations and network errors alike: drop the beacon, never the page
  }
}

/** Convenience: the distinct topics of the top-N shelf cards. */
export function topTopics(cards: Array<{topics?: string[]}>, n = 3): string[] {
  const out = new Set<string>()
  for (const c of cards.slice(0, n)) for (const t of c.topics ?? []) out.add(t.toLowerCase())
  return [...out]
}
